import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  TextField,
  Button,
  Avatar,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  // ToggleButton,
  // ToggleButtonGroup,
} from '@mui/material'
import {
  ArrowUpward,
  Delete,
  Archive,
  Person,
  Assignment,
  MoreVert,
  ExpandMore,
  ExpandLess,
  InfoOutlined,
  Code,
  AttachFile,
  CloudUpload,
  CloudDownload,
  DataObject,
  FactCheck,
  Language,
} from '@mui/icons-material'
import { formatDistanceToNow } from 'date-fns'
import { useAuth } from '../hooks/useAuth'
import { useTicketDetails } from '../hooks/useTicketDetails'
import { useTicketPermissions, useMessageExpansion, useConversationScroll } from '../hooks/conversation'
import { useStreamSubmission } from '../hooks/useStreamSubmission'
import { ticketsAPI, agentsAPI, usersAPI, adminAgentsAPI, buildSSEUrl } from '../services/api'
import { notifyDisputeCreated, notifyDisputesCreated } from '../utils/notifyParent'
import { messagesSignature } from '../utils/messagesSignature'
import MessageContent from './MessageContent'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import FormOverlay from './FormOverlay'
import TicketReviewPanel from './TicketReviewPanel'
import AttachmentsModal from './AttachmentsModal'
import ConversationMessage from './ConversationMessage'
import yaml from 'js-yaml'
import LoadingSpinner from './LoadingSpinner'
import ErrorAlert from './ErrorAlert'
import InfoSidePanel from './InfoSidePanel'
import SearchableSelect from './SearchableSelect'
import ConversationBanner from './ConversationBanner'
import ISO_639_1_LANGUAGES from '../utils/languages'

const LANGUAGE_OPTIONS = ISO_639_1_LANGUAGES.map((l) => ({
  value: l.code,
  label: `${l.name} (${l.nativeName})`,
}))

const getStatusColor = (status, theme) => {
  const baseColor = (() => {
    switch (status?.toLowerCase()) {
      case 'new':
        return 'info'
      case 'processing':
        return 'warning'
      case 'waiting for user':
      case 'waiting_for_user':
        return 'success'
      case 'waiting for external':
      case 'waiting_for_external':
      case 'waiting':
        return 'secondary'
      case 'complete':
      case 'completed':
        return 'default'
      case 'error':
      case 'failed':
        return 'error'
      default:
        return 'default'
    }
  })()

  // Return muted colors for night mode accessibility
  if (theme?.palette?.mode === 'dark') {
    switch (baseColor) {
      case 'info':
        return { color: 'var(--agentflow-info-muted)' }
      case 'warning':
        return { color: 'var(--agentflow-warning-muted)' }
      case 'success':
        return { color: 'var(--agentflow-success-muted)' }
      case 'error':
        return { color: 'var(--agentflow-error-muted)' }
      default:
        return baseColor
    }
  }

  return baseColor
}

// Fun thinking phrases for Izzi
const THINKING_PHRASES = [
  'thinking',
  // 'decoding the matrix',
  // 'brewing brain-tea',
  // 'knitting neuron sweaters',
  // 'doing quantum hula-hoops',
  // 'marinating in genius sauce',
  // 'tickling the data dragons',
  // 'running mental karaoke',
  // 'tuning her curiosity kazoo',
  // 'whispering to the algorithms',
  // 'polishing crystal ball bearings',
  // 'conducting a synapse symphony',
  // 'stir-frying the facts',
  // 'practicing mind yoga',
  // 'balancing ledgers',
  // 'counting coins',
  // 'chasing invoices',
  // 'spotting spare cents',
  // 'bankrolling ideas',
  // 'closing books',
  // 'tallying totals',
  // 'pricing the risk',
  // 'filing receipts',
  // 'budgeting brainwaves',
  // 'adding interest',
  // 'rounding decimals',
  // 'auditing thoughts',
  // 'netting profits',
  // 'posting journals',
  // 'trimming expenses',
  // 'forecasting fun',
  // 'reconciling reality',
  // 'clearing checks',
  // 'crediting creativity'
]

const getRandomThinkingPhrase = () => {
  return THINKING_PHRASES[Math.floor(Math.random() * THINKING_PHRASES.length)]
}


const ConversationView = ({
  ticketId,
  onTicketUpdated,
  embedMode = false,
  readOnly = false
}) => {
  const { user, patchUser } = useAuth()
  const navigate = useNavigate()

  // Use the ticket details hook for data management
  const {
    ticket,
    loading,
    error,
    isProcessing,
    refresh: onRefresh,
    triggerImmediatePoll,
    // Polling control for user interactions
    pausePolling,
    resumePolling,
    resumePollingAfterDelay,
    onImmediateThinkingChange,
    injectOptimisticMessage,
    injectTicketStatusUpdate,
    injectChildTicket,
    injectTicketDataUpdate,
  } = useTicketDetails({ ticketId, onTicketUpdated, currentUserEmail: user?.email })
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  // Panel overlays content only when viewport is wide enough that it won't bleed over the conversation
  const isPanelOverlay = useMediaQuery('(min-width: 1100px)')
  const [response, setResponse] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [pendingMessage, setPendingMessage] = useState(null)
  const [headerExpanded, setHeaderExpanded] = useState(false)
  const [viewMode] = useState('conversation') // 'conversation' or 'logs'
  const [immediateThinking, setImmediateThinking] = useState(false) // Show thinking bubble immediately after user response
  const [thinkingPhrase, setThinkingPhrase] = useState('') // Current thinking phrase
  const [lastAgentMessageCount, setLastAgentMessageCount] = useState(0) // Track agent message count to detect new responses
  const [directStreamContent, setDirectStreamContent] = useState('') // LLM tokens streamed directly via POST SSE
  const [streamingAction, setStreamingAction] = useState('') // Current tool-action text from tool_action SSE events
  const [streamingOutputs, setStreamingOutputs] = useState([]) // last_output items streamed in real-time via tool_output SSE events
  const [streamingLogs, setStreamingLogs] = useState([]) // log entries streamed live via log_event SSE events
  const streamAbortRef = useRef(null) // AbortController for the active POST SSE stream
  const chunkBufferRef = useRef('') // Raw incoming LLM text not yet rendered (throttled display)
  const fullStreamedContentRef = useRef('') // Complete streamed text accumulated for optimistic insert
  const renderTimerRef = useRef(null) // Interval that drains chunkBufferRef into directStreamContent
  const lastResetTicketIdRef = useRef(null) // Track which ticket we last reset the count for
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null) // Menu anchor for more options
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false)
  const [pendingLanguage, setPendingLanguage] = useState('en')
  const [isDragging, setIsDragging] = useState(false) // Track drag state
  const [agentAcceptsFiles, setAgentAcceptsFiles] = useState(false) // Track if agent accepts files
  const [isUploadingFile, setIsUploadingFile] = useState(false) // Track file upload state
  const [formDataByFormId, setFormDataByFormId] = useState({}) // Store form data by form_id to persist across re-renders
  const [translatedMessages, setTranslatedMessages] = useState(null) // null = show original
  const [isTranslating, setIsTranslating] = useState(false)
  const translationCacheRef = useRef(null) // cached translation, survives show/hide toggles
  const [renderedPromptsOpen, setRenderedPromptsOpen] = useState(false)
  const [renderedPromptsData, setRenderedPromptsData] = useState(null)
  const [renderedPromptsLoading, setRenderedPromptsLoading] = useState(false)
  const [renderedPromptsError, setRenderedPromptsError] = useState(null)
  const [renderedPromptsExpanded, setRenderedPromptsExpanded] = useState(0)
  const [reviewOpen, setReviewOpen] = useState(false)

  const handleViewRenderedPrompt = async () => {
    setMoreMenuAnchor(null)
    setRenderedPromptsOpen(true)
    setRenderedPromptsLoading(true)
    setRenderedPromptsError(null)
    setRenderedPromptsData(null)
    setRenderedPromptsExpanded(0)
    try {
      const data = await adminAgentsAPI.getRenderedPrompts(ticket.id)
      setRenderedPromptsData(data.prompts)
    } catch {
      setRenderedPromptsError('Failed to load rendered prompts. Please try again.')
    } finally {
      setRenderedPromptsLoading(false)
    }
  }

  // Use extracted hooks for expansion and scroll management
  const {
    expandedMessages,
    expandedForms,
    setExpandedForms,
    toggleMessageExpansion
  } = useMessageExpansion()

  const {
    messagesEndRef,
    conversationContainerRef,
    thinkingBubbleRef,
    scrollToBottom,
    scrollToThinkingBubble,
    saveScrollPosition,
    resetScrollState,
    clearSavedPosition,
    wasAtBottom,
    generateContentHash,
    previousContentHashRef,
    userIsScrollingRef
  } = useConversationScroll()
  const [renderedMessages, setRenderedMessages] = useState([]) // Store rendered messages to prevent re-render
  const [renderedTicketId, setRenderedTicketId] = useState(null) // Track which ticket is rendered
  const [activeFormSpec, setActiveFormSpec] = useState(null) // Track active form to show in overlay
  const [activeQuickReplies, setActiveQuickReplies] = useState(null) // Track quick reply options to show as chips
  const [activeFormMessageIndex, setActiveFormMessageIndex] = useState(null) // Track message index containing the form
  const [activeToast, setActiveToast] = useState(null)
  const [toastDismissed, setToastDismissed] = useState(false)
  const [formExpanded, setFormExpanded] = useState(false) // Track whether the active form is expanded or collapsed
  const [attachmentsModalOpen, setAttachmentsModalOpen] = useState(false) // Track attachments modal state
  const [showSystemInfo, setShowSystemInfo] = useState(() => {
    // Only load from localStorage for admins, default to false for non-admins
    try {
      const stored = localStorage.getItem('agentflow-show-system-info')
      return stored ? JSON.parse(stored) : false
    } catch {
      return false
    }
  })
  const [showInfoPanel, setShowInfoPanel] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('agentflow-show-info-panel')) || false
    } catch {
      return false
    }
  })
  // Removed selectedTicketView - we always auto-select active child or parent

  // Notes functionality state
  const [notesData, setNotesData] = useState([]) // All notes for this ticket
  const [notesModalOpen, setNotesModalOpen] = useState(false) // Notes modal state
  const [selectedMessageIndex, setSelectedMessageIndex] = useState(null) // Message index for notes
  const [hoveredMessageIndex, setHoveredMessageIndex] = useState(null) // Track hovered message
  const [noteText, setNoteText] = useState('') // Text for new note
  const [savingNote, setSavingNote] = useState(false) // Track note save state
  const fileInputRef = useRef(null)
  const lastOutputLengthRef = useRef(0)
  const lastFormYamlRef = useRef(null) // Track last parsed form YAML to prevent unnecessary re-parsing
  const lastScrolledFormIdRef = useRef(null) // Track last form we scrolled for
  const lastFormScrollTimeRef = useRef(0) // Track when we last scrolled for a form

  // Helper function to get the ticket that should be displayed
  // RULE: Always show active child if it exists, otherwise show parent
  const getDisplayTicket = () => {
    if (!ticket) return null
    if (ticket.active_child && ticket.child_tickets) {
      const activeChild = ticket.child_tickets.find(child => child.id === ticket.active_child)
      return activeChild || ticket
    }
    return ticket
  }
  const displayTicket = getDisplayTicket()

  // SSE stream submission — single implementation shared by all three submission handlers
  const { openStream } = useStreamSubmission({
    displayTicket,
    user,
    streamAbortRef,
    renderTimerRef,
    chunkBufferRef,
    fullStreamedContentRef,
    setSubmitting,
    setImmediateThinking,
    setDirectStreamContent,
    setStreamingAction,
    setStreamingOutputs,
    setStreamingLogs,
    setLastAgentMessageCount,
    setPendingMessage,
    onImmediateThinkingChange,
    setThinkingPhrase,
    getRandomThinkingPhrase,
    resumePolling,
    resumePollingAfterDelay,
    triggerImmediatePoll,
    injectOptimisticMessage,
    injectTicketStatusUpdate,
    injectChildTicket,
    injectTicketDataUpdate,
  })

  // Use extracted hook for ticket permissions
  const {
    isAdmin,
    canAddNotes,
    canRespond,
    canArchive,
    canPermanentlyDelete,
    getReplyDisabledMessage
  } = useTicketPermissions(ticket, displayTicket, user)

  // Get existing notes for the form message
  const formNotes = useMemo(() => {
    if (activeFormMessageIndex === null) return []
    return notesData.filter(note => note.message_index === activeFormMessageIndex)
  }, [activeFormMessageIndex, notesData])

  // Load notes for the current ticket
  const loadNotes = useCallback(async () => {
    if (!ticket) return
    try {
      // Fetch notes from parent ticket
      const parentNotes = await ticketsAPI.getTicketNotes(ticket.id)
      // Add source_ticket_id to each note for lookup
      const notesWithSource = parentNotes.map(note => ({
        ...note,
        source_ticket_id: ticket.id
      }))

      // Fetch notes from all child tickets (if any)
      if (ticket.child_tickets && ticket.child_tickets.length > 0) {
        for (const child of ticket.child_tickets) {
          try {
            const childNotes = await ticketsAPI.getTicketNotes(child.id)
            // Add child notes with source_ticket_id
            childNotes.forEach(note => {
              notesWithSource.push({
                ...note,
                source_ticket_id: child.id
              })
            })
          } catch (error) {
            console.warn(`Failed to load notes for child ticket ${child.id}:`, error)
            // Continue with other children
          }
        }
      }

      setNotesData(notesWithSource)
    } catch (error) {
      console.error('Failed to load notes:', error)
      // If user doesn't have permission or notes endpoint fails, silently continue
      setNotesData([])
    }
  }, [ticket])

  // Translate conversation messages
  const isTranslatingRef = useRef(false)
  const handleTranslate = useCallback(async () => {
    if (!ticket || isTranslatingRef.current) return
    // Use cached translation if available — avoids repeat API calls on toggle
    if (translationCacheRef.current) {
      setTranslatedMessages(translationCacheRef.current)
      return
    }
    isTranslatingRef.current = true
    const viewerLang = user?.preferred_language || 'en'
    setIsTranslating(true)
    try {
      const result = await ticketsAPI.translateTicket(ticket.id, viewerLang)
      const messages = result.messages || null
      translationCacheRef.current = messages
      setTranslatedMessages(messages)
    } catch (error) {
      console.error('Failed to translate ticket:', error)
    } finally {
      isTranslatingRef.current = false
      setIsTranslating(false)
    }
  }, [ticket, user])

  const handleSaveLanguage = async () => {
    try {
      await usersAPI.updateUser(user.email, { preferred_language: pendingLanguage })
      patchUser({ preferred_language: pendingLanguage })
      setLanguageDialogOpen(false)
    } catch (error) {
      console.error('Failed to update language:', error)
    }
  }

  // Reset translation and auto-translate when ticket or user language changes
  useEffect(() => {
    setTranslatedMessages(null)
    translationCacheRef.current = null
    isTranslatingRef.current = false
    if (!ticket || !user) return
    const viewerLang = user.preferred_language || 'en'
    const ticketLang = ticket.data?.preferred_language || 'en'
    if (viewerLang !== ticketLang) {
      handleTranslate()
    }
  }, [ticket?.id, user?.preferred_language]) // eslint-disable-line react-hooks/exhaustive-deps

  // Helper functions for message filtering

  // System Info Tags - add new tags here to filter them from conversation view
  const SYSTEM_INFO_TAGS = [
    'Memory',
    'Knowledge',
    'api',
    'API',
    'Handover',
    'AgentHandback',
    'Complete',
    'CaptureFact',
  ]

  const hasApiTag = (message) => {
    if (!message?.messageMetadata?.tags) return false
    const tags = message.messageMetadata.tags.split(',').map(t => t.trim().toLowerCase())
    return tags.some(tag => tag.includes('api'))
  }

  const isSystemInfoMessage = (message) => {
    if (!message?.messageMetadata?.tags) return false
    const tags = message.messageMetadata.tags

    // Check if any of the system info tags are present
    return SYSTEM_INFO_TAGS.some(tag => tags.includes(tag)) || hasApiTag(message)
  }

  // Check if a child ticket is actively processing
  const isChildProcessing = useCallback(() => {
    if (!ticket?.active_child || !ticket?.child_tickets) return false

    const activeChild = ticket.child_tickets.find(child => child.id === ticket.active_child)
    if (!activeChild) return false

    // Child is processing if it's assigned to agent and not complete/error
    return activeChild.role === 'agent' &&
           activeChild.status !== 'complete' &&
           activeChild.status !== 'error'
  }, [ticket?.active_child, ticket?.child_tickets])

  // Get the conversation messages to display - use server's merged thread
  const displayMessages = useMemo(() => {
    if (!displayTicket) return []

    // If server provided a merged conversation thread, use it
    // (Server should merge ALL child tickets and inject metadata tags)
    // Fall back to the parent ticket's thread (not the child's empty thread) so the
    // conversation doesn't blank out when active_child is first set with no messages yet.
    const messages = ticket?.merged_conversation_thread
      ?? ticket?.conversation_thread
      ?? []

    // Sort by timestamp so display order matches DB order.
    // Messages without a timestamp keep their relative (array) position.
    return [...messages].sort((a, b) => {
      if (!a.timestamp && !b.timestamp) return 0
      if (!a.timestamp) return 1
      if (!b.timestamp) return -1
      return a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0
    })
  }, [ticket, displayTicket])

  // Detect user scrolling
  // Scroll detection is handled by useConversationScroll hook

  useEffect(() => {
    if (!displayTicket) {
      resetScrollState()
      return
    }

    const currentContentHash = generateContentHash(displayTicket)
    const previousContentHash = previousContentHashRef.current

    // If content hasn't changed, don't do anything - let user control scroll
    if (previousContentHash && currentContentHash === previousContentHash) {
      return
    }

    // Content has changed - save current scroll position before updating
    if (previousContentHash) {
      saveScrollPosition()
    }

    // Update content hash
    previousContentHashRef.current = currentContentHash

    // Don't auto-scroll if user is actively scrolling
    if (userIsScrollingRef.current) {
      return
    }

    // Scroll to bottom for new messages, pending messages, or processing state
    if (displayMessages?.length || pendingMessage || isProcessing) {
      // Always auto-scroll for pending messages (user just sent a message) or processing state
      const shouldAutoScroll = !previousContentHash || // First load
        pendingMessage || // User just sent a message - always scroll
        (isProcessing && displayTicket?.role === 'agent') || // Agent is processing - always scroll
        wasAtBottom() // User was at bottom

      // Also auto-scroll when new agent messages are added (coming from processing state)
      const hasNewAgentMessages = previousContentHash &&
        displayMessages?.length > 0 &&
        !isProcessing && // Not currently processing
        displayTicket?.role === 'user' && // Now waiting for user
        displayTicket?.status === 'waiting'

      if (shouldAutoScroll || hasNewAgentMessages) {
        // Clear saved position when auto-scrolling to prevent restoration issues
        clearSavedPosition()
        // Use instant scroll (not smooth) to avoid an animated "jump" when the final
        // message replaces the streaming bubble — the two positions differ in height and
        // smooth animation makes the shift visually jarring. We keep the 100ms delay so
        // layout (especially forms) has time to reflow before we measure scrollHeight.
        setTimeout(() => {
          const container = conversationContainerRef.current
          if (container) container.scrollTop = container.scrollHeight
        }, 100)
      }
    }
  }, [displayTicket, pendingMessage, isProcessing, generateContentHash, saveScrollPosition, resetScrollState, wasAtBottom, clearSavedPosition, displayMessages])

  // Reset live streaming facts only when navigating to a different root ticket.
  // Watch ticket?.id (root) not displayTicket?.id — displayTicket changes during child agent turns
  // Reset streaming logs when the ticket changes.
  useEffect(() => {
    setStreamingLogs([])
  }, [ticket?.id])

  // Scroll to bottom when user sends a message (pending message appears)
  useEffect(() => {
    if (pendingMessage) {
      // Immediate scroll to show the pending message loader
      setTimeout(scrollToBottom, 50)
    }
  }, [pendingMessage])

  // Scroll to thinking bubble when processing starts
  useEffect(() => {
    if (displayTicket?.role === 'agent' && displayTicket?.status !== 'complete' && displayTicket?.status !== 'error' && displayTicket?.status !== 'new') {
      // NOTE: We no longer clear immediateThinking here - let the message detection
      // useEffect handle that (line 1514) to prevent premature clearing

      // Set a thinking phrase if we don't have one
      if (!thinkingPhrase) {
        setThinkingPhrase(getRandomThinkingPhrase())
      }

      // Small delay to ensure the thinking bubble is rendered
      setTimeout(() => {
        if (thinkingBubbleRef.current) {
          scrollToThinkingBubble()
        } else {
          scrollToBottom() // Fallback to bottom scroll
        }
      }, 200)
    }
  }, [isProcessing, displayTicket?.role, displayTicket?.status, thinkingPhrase, scrollToThinkingBubble, scrollToBottom])

  // Clear immediate thinking state when agent responds and message is rendered
  useEffect(() => {
    // Never auto-clear while a stream is active — the stream manages its own thinking state
    if (submitting) return

    if (immediateThinking &&
        displayTicket?.role === 'user' &&
        displayTicket?.status === 'waiting' &&
        displayMessages) {

      // Count current visible (non-system) agent messages
      const currentAgentMsgCount = displayMessages.filter(m =>
        m.role === 'assistant' && !isSystemInfoMessage(m)
      ).length

      // Only clear thinking bubble if we have a NEW visible agent message AND no child is processing
      if (currentAgentMsgCount > lastAgentMessageCount && !isChildProcessing()) {
        // Delay to ensure message is fully rendered (especially child messages with chips)
        const timeoutId = setTimeout(() => {
          console.debug('Clearing immediate thinking state after agent response')
          setImmediateThinking(false)
          onImmediateThinkingChange(false)
          setThinkingPhrase('')
        }, 1000) // Longer delay for complex messages (child messages, forms, etc.)

        return () => clearTimeout(timeoutId)
      }
    }
  }, [submitting, immediateThinking, displayTicket?.role, displayTicket?.status, displayMessages, lastAgentMessageCount, isChildProcessing, onImmediateThinkingChange])

  // Clear immediate thinking state when ticket is complete
  useEffect(() => {
    // Never auto-clear while a stream is active — the stream manages its own thinking state
    if (submitting) return
    if (immediateThinking && displayTicket?.status === 'complete') {
      console.debug('Clearing immediate thinking state due to ticket completion')
      setImmediateThinking(false)
      onImmediateThinkingChange(false)
      setThinkingPhrase('') // Clear the phrase
    }
  }, [submitting, immediateThinking, displayTicket?.status, onImmediateThinkingChange])

  // Recover from idle/sleep: when the user returns to the tab or the network comes back online
  // while the ticket is still processing, fetch the current state immediately. This is a
  // no-op if polling is paused (stream is healthy), but fires after the watchdog has already
  // recovered the stream — catching any residual "stuck thinking" state.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && (immediateThinking || isProcessing)) {
        triggerImmediatePoll()
      }
    }
    const handleOnline = () => {
      if (immediateThinking || isProcessing) {
        triggerImmediatePoll()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('online', handleOnline)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('online', handleOnline)
    }
  }, [immediateThinking, isProcessing, triggerImmediatePoll])

  // Reset agent message count when ticket changes (not when messages change!)
  useEffect(() => {
    const currentTicketId = displayTicket?.id

    // Only reset if the ticket ID actually changed
    if (currentTicketId && currentTicketId !== lastResetTicketIdRef.current && displayMessages) {
      const agentMsgCount = displayMessages.filter(m =>
        m.role === 'assistant' && !isSystemInfoMessage(m)
      ).length
      setLastAgentMessageCount(agentMsgCount)
      lastResetTicketIdRef.current = currentTicketId
    }
  }, [displayTicket?.id, displayMessages]) // Need displayMessages to read current value

  // Notify parent when dispute(s) are created (embed mode only)
  const notifiedDisputeIdsRef = useRef(new Set())
  useEffect(() => {
    if (!embedMode || !displayMessages) return

    for (const message of displayMessages) {
      const tags = message?.messageMetadata?.tags
      if (!tags || !tags.includes('createDispute')) continue

      // Multi-invoice path: dispute_ids is a comma-separated string of IDs
      const disputeIdsRaw = message?.messageMetadata?.dispute_ids
      if (disputeIdsRaw) {
        const key = `multi:${disputeIdsRaw}`
        if (!notifiedDisputeIdsRef.current.has(key)) {
          notifiedDisputeIdsRef.current.add(key)
          const ids = disputeIdsRaw.split(',').map((id) => Number(id.trim())).filter(Boolean)
          notifyDisputesCreated(ids)
        }
        continue
      }

      // Legacy single-invoice path: dispute_id is a single ID
      const disputeId = message?.messageMetadata?.dispute_id
      if (disputeId && !notifiedDisputeIdsRef.current.has(disputeId)) {
        notifiedDisputeIdsRef.current.add(disputeId)
        notifyDisputeCreated(disputeId)
      }
    }
  }, [embedMode, displayMessages])

  // Scroll to immediate thinking bubble when it appears
  useEffect(() => {
    if (immediateThinking && !userIsScrollingRef.current) {
      setTimeout(scrollToBottom, 100) // Scroll to show immediate thinking bubble
    }
  }, [immediateThinking])

  // Auto-scroll when new last_output items are added
  useEffect(() => {
    const outputsArray = getDisplayOutputs()
    const currentLength = outputsArray.length
    // Scroll if we have items (either new items or existing items - any progress messages)
    if (currentLength > 0) {
      // Update the ref to the new length
      lastOutputLengthRef.current = currentLength

      // Scroll to bottom when output exists or changes
      // We don't check userIsScrollingRef here because progress updates should always scroll
      // Wait longer (1000ms) to ensure fade-in animations complete (0.8s + stagger delays)
      let rafId
      let timerId
      rafId = requestAnimationFrame(() => {
        // First, scroll immediately to show content
        scrollToBottom()
        // Then scroll again after animations complete to ensure visibility
        timerId = setTimeout(scrollToBottom, 1000)
      })

      return () => {
        if (rafId) cancelAnimationFrame(rafId)
        if (timerId) clearTimeout(timerId)
      }
    }

    // Update ref even if length didn't increase (handles array replacement)
    lastOutputLengthRef.current = currentLength
  }, [displayTicket?.last_output, streamingOutputs])

  // Abort any active POST SSE stream, clear render timer, and reset streaming state
  // when the ticket changes or on unmount. Running on ticketId change (not just unmount)
  // prevents stale streaming content and pending messages from a previous ticket
  // appearing in the new ticket's conversation.
  useEffect(() => {
    setDirectStreamContent('')
    setStreamingAction('')
    setStreamingOutputs([])
    setPendingMessage(null)
    setImmediateThinking(false)
    chunkBufferRef.current = ''
    fullStreamedContentRef.current = ''

    return () => {
      if (streamAbortRef.current) {
        streamAbortRef.current.abort()
      }
      if (renderTimerRef.current) {
        clearInterval(renderTimerRef.current)
        renderTimerRef.current = null
      }
    }
  }, [ticketId])

  // Auto-scroll the conversation container as streaming content grows
  useEffect(() => {
    if (directStreamContent && !userIsScrollingRef.current) {
      const container = conversationContainerRef.current
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    }
  }, [directStreamContent])

  // Clear pending message if it appears in the conversation thread
  useEffect(() => {
    if (pendingMessage && displayMessages) {
      const messageExists = displayMessages.some(msg =>
        msg.content === pendingMessage.content &&
        msg.role === pendingMessage.role &&
        Math.abs(new Date(msg.timestamp) - new Date(pendingMessage.timestamp)) < 10000 // Within 10 seconds
      )
      if (messageExists) {
        // Sync renderedMessages with displayMessages before clearing the pending message.
        // The renderedMessages update effect only checks count, so if an optimistic agent
        // message already made renderedMessages the same length as the incoming server thread
        // (which now includes the confirmed user message), the count check won't fire and the
        // user's message would vanish from both renderedMessages and pendingMessage.
        setRenderedMessages(displayMessages)
        setPendingMessage(null)
      }
    }
  }, [displayMessages, pendingMessage])

  // Only update rendered messages when the displayed ticket changes or the thread
  // meaningfully changes. A count-only check froze same-length divergences on screen
  // (agent reply sorted above the user's message, streamed text replaced by the final
  // form message) until a page refresh — the signature catches reorders, content
  // changes, and optimistic→server id swaps while still skipping identical polls.
  useEffect(() => {
    if (!displayTicket) {
      setRenderedMessages([])
      setRenderedTicketId(null)
      return
    }

    const currentTicketId = displayTicket.id

    const shouldUpdate =
      renderedTicketId !== currentTicketId || // Different ticket
      messagesSignature(renderedMessages) !== messagesSignature(displayMessages) // Thread changed

    if (shouldUpdate) {
      setRenderedMessages(displayMessages)
      setRenderedTicketId(currentTicketId)
    }
  }, [displayTicket, displayMessages, renderedTicketId, renderedMessages])

  // Detect form in the last assistant message and show overlay
  // Extract YAML content and store in state
  useEffect(() => {
    if (!displayTicket || !displayMessages || displayMessages.length === 0) {
      if (lastFormYamlRef.current !== null) {
        lastFormYamlRef.current = null
        setActiveFormSpec(null)
        setActiveFormMessageIndex(null)
      }
      return
    }

    // Search backwards for the most recent assistant message containing a form block
    // that has NOT yet been answered by the user.
    //
    // We can't just use the last assistant message because thread_message events
    // (e.g. "Dispute Type Matched") arrive after thread_chunk_end and may push a
    // short assistant context message to the end of the list.
    //
    // Handles three LLM output variations:
    //   1. Proper closing:          ```form\n...\n```
    //   2. Closing without newline: ```form\n...```   (LLM omits trailing newline)
    //   3. No closing at all:       ```form\n...      (LLM truncates the fence)
    const formBlockRegex = /```form\n([\s\S]*?)(?=\n```|```|$)/
    let formMessage = null
    let formMessageIndex = null
    for (let i = displayMessages.length - 1; i >= 0; i--) {
      const msg = displayMessages[i]
      if (msg.role === 'assistant' && msg.content?.match(formBlockRegex)) {
        // Only show this form if no user message has been sent after it
        const hasUserReply = displayMessages.slice(i + 1).some(m => m.role === 'user')
        if (!hasUserReply) {
          formMessage = msg
          formMessageIndex = i
        }
        break
      }
    }

    if (formMessage) {
      const yamlContent = formMessage.content.match(formBlockRegex)[1]
      if (yamlContent !== lastFormYamlRef.current) {
        lastFormYamlRef.current = yamlContent
        setActiveFormSpec(yamlContent)
        setActiveFormMessageIndex(formMessageIndex)
      }
    } else {
      if (lastFormYamlRef.current !== null) {
        lastFormYamlRef.current = null
        setActiveFormSpec(null)
        setActiveFormMessageIndex(null)
      }
    }
  }, [displayMessages])

  // Detect ```quick_replies blocks in last assistant message (mirrors form detection above)
  useEffect(() => {
    const quickReplyRegex = /```quick_replies\n([\s\S]*?)(?=\n```|```|$)/
    for (let i = displayMessages.length - 1; i >= 0; i--) {
      const msg = displayMessages[i]
      if (msg.role === 'assistant' && msg.content?.match(quickReplyRegex)) {
        const hasUserReply = displayMessages.slice(i + 1).some(m => m.role === 'user')
        if (!hasUserReply) {
          try {
            const yamlContent = msg.content.match(quickReplyRegex)[1]
            const parsed = yaml.load(yamlContent)
            if (Array.isArray(parsed)) {
              setActiveQuickReplies(parsed.map(String))
              return
            }
          } catch (e) {
            console.error('[ConversationView] Failed to parse quick_replies YAML:', e)
          }
        }
        break
      }
    }
    setActiveQuickReplies(null)
  }, [displayMessages])

  // Detect agentflow_banner blocks in last assistant message
  useEffect(() => {
    const toastRegex = /```agentflow_banner\n([\s\S]*?)\n```/
    let found = null
    for (let i = displayMessages.length - 1; i >= 0; i--) {
      const msg = displayMessages[i]
      if (msg.role === 'assistant' && msg.content?.match(toastRegex)) {
        const hasUserReply = displayMessages.slice(i + 1).some(m => m.role === 'user')
        if (!hasUserReply) {
          try {
            const yamlContent = msg.content.match(toastRegex)[1]
            const parsed = yaml.load(yamlContent)
            if (parsed && typeof parsed === 'object' && parsed.title && parsed.body) {
              found = parsed
            }
          } catch (e) {
            console.error('[ConversationView] Failed to parse agentflow_banner YAML:', e)
          }
        }
        break
      }
    }
    setActiveToast(prev => {
      if (JSON.stringify(prev) !== JSON.stringify(found)) {
        setToastDismissed(false)
      }
      return found
    })
  }, [displayMessages])

  // Parse the YAML with useMemo to prevent re-parsing on every render.
  // Track the parse error too so the UI can show a fallback instead of
  // silently dropping the form when the LLM emits malformed YAML.
  const { parsedFormSpec, formParseError } = useMemo(() => {
    if (!activeFormSpec || typeof activeFormSpec !== 'string') {
      return { parsedFormSpec: activeFormSpec, formParseError: null }
    }

    try {
      return { parsedFormSpec: yaml.load(activeFormSpec), formParseError: null }
    } catch (error) {
      console.error('[ConversationView] Failed to parse form YAML:', error)
      return { parsedFormSpec: null, formParseError: error.message }
    }
  }, [activeFormSpec])

  // Scroll to bottom when form appears (only once per form)
  useEffect(() => {
    if (parsedFormSpec) {
      const formId = parsedFormSpec.form_id || JSON.stringify(parsedFormSpec)
      const now = Date.now()

      // Only scroll if:
      // 1. This is a new form (different ID), AND
      // 2. It's been at least 2 seconds since the last form scroll (prevent rapid scrolling)
      if (lastScrolledFormIdRef.current !== formId && (now - lastFormScrollTimeRef.current) > 2000) {
        lastScrolledFormIdRef.current = formId
        lastFormScrollTimeRef.current = now
        setFormExpanded(false) // Start new forms collapsed

        // Scroll to bottom with a delay to ensure content is rendered
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
        }, 300)
      }
    } else if (!parsedFormSpec) {
      // Reset when form is closed
      lastScrolledFormIdRef.current = null
    }
  }, [parsedFormSpec])

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      const relativeTime = formatDistanceToNow(date, { addSuffix: true })
      const actualDate = date.toLocaleDateString()
      return `${relativeTime} (${actualDate})`
    } catch {
      return 'Invalid date'
    }
  }

  // Check if the agent accepts file uploads
  useEffect(() => {
    const checkAgentFileSupport = async () => {
      // Check both ticket.type and ticket.ticket_type for compatibility
      const ticketType = ticket?.type || ticket?.ticket_type

      if (!ticketType) return

      try {
        const agentCapabilities = await agentsAPI.getAgentCapabilities(ticketType)
        const acceptsFiles = agentCapabilities?.accepts_file_uploads || false
        setAgentAcceptsFiles(acceptsFiles)
      } catch (error) {
        console.log('Could not check agent file support:', error)
        setAgentAcceptsFiles(false)
      }
    }

    checkAgentFileSupport()
  }, [ticket?.type, ticket?.ticket_type])

  // Debug logging for notes permissions (only log once on mount or user change)
  useEffect(() => {
    if (user) {
      console.log('[Notes] Permission check - canAddNotes:', canAddNotes, 'roles:', user.application_roles)
    }
  }, [user?.email, canAddNotes, user])

  // Load notes once per ticket open. Deliberately keyed on ticket id only —
  // loadNotes is omitted so routine status/thread updates don't refetch notes.
  // The add-note path updates notesData optimistically.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (ticket && canAddNotes) {
      loadNotes()
    }
  }, [ticket?.id, canAddNotes])

  // Compute messages and filter system info (must be before early returns)
  const messages = renderedMessages
  const shouldShowPendingMessage = useMemo(() => {
    if (!pendingMessage) return false
    const matchFn = msg =>
      msg.content === pendingMessage.content &&
      Math.abs(new Date(msg.timestamp) - new Date(pendingMessage.timestamp)) < 10000
    // Check both displayMessages and renderedMessages:
    // - displayMessages catches the case where server data arrives before renderedMessages updates
    // - renderedMessages catches the case where an optimistic inject already added the message
    //   but a stale poll then overwrites displayMessages (removing it), causing a duplicate
    return !displayMessages.some(matchFn) && !renderedMessages.some(matchFn)
  }, [pendingMessage, displayMessages, renderedMessages])

  // Merge pending message and sort by timestamp so injected optimistic assistant messages
  // (from turn_end / stream_message) don't end up after the user's pending message.
  const allMessagesUnfiltered = useMemo(() => {
    const combined = shouldShowPendingMessage ? [...messages, pendingMessage] : messages
    if (!shouldShowPendingMessage) return combined
    return [...combined].sort((a, b) => {
      const tA = a?.timestamp ? new Date(a.timestamp).getTime() : 0
      const tB = b?.timestamp ? new Date(b.timestamp).getTime() : 0
      return tA - tB
    })
  }, [messages, pendingMessage, shouldShowPendingMessage])

  // Add original index to each message before filtering, so notes can be matched correctly
  const allMessages = useMemo(() => {
    // First, tag each message with its original index from displayMessages
    const messagesWithOriginalIndex = allMessagesUnfiltered.map((msg, originalIndex) => ({
      ...msg,
      originalIndex  // This is the index that notes reference
    }))

    // Then filter if needed
    if (!isAdmin) {
      return messagesWithOriginalIndex.filter(msg => !isSystemInfoMessage(msg))
    }
    if (showSystemInfo) {
      return messagesWithOriginalIndex
    } else {
      return messagesWithOriginalIndex.filter(msg => !isSystemInfoMessage(msg))
    }
  }, [allMessagesUnfiltered, showSystemInfo, isAdmin])

  const handleFileUpload = async (file) => {
    if (!file || !displayTicket || immediateThinking || isProcessing) return

    pausePolling('file upload')
    setIsUploadingFile(true)

    if (!userIsScrollingRef.current) {
      setTimeout(scrollToBottom, 100)
    }

    const formData = new FormData()
    formData.append('file', file)

    setPendingMessage({ role: 'user', content: `📎 ${file.name}`, timestamp: new Date().toISOString(), pending: true, file })
    setLastAgentMessageCount(displayMessages.filter(m => m.role === 'assistant' && !isSystemInfoMessage(m)).length)

    await openStream({
      formData,
      url: buildSSEUrl(`/files/ticket/${displayTicket.id}/upload-stream`),
      defaultErrorMessage: 'File upload failed. Please try again.',
      onOpen: () => setIsUploadingFile(false),
    })

    // Clear spinner if onOpen never fired (connection failed before headers arrived)
    setIsUploadingFile(false)
  }

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0]
    if (file) {
      handleFileUpload(file)
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDragOver = (event) => {
    event.preventDefault()
    event.stopPropagation()

    // Only update state if it's actually changing - prevent unnecessary re-renders
    if (!isDragging && agentAcceptsFiles && canRespond() && !immediateThinking && !isProcessing) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (event) => {
    event.preventDefault()
    event.stopPropagation()

    // Only handle drag leave if we're actually leaving the container
    const rect = conversationContainerRef.current?.getBoundingClientRect()
    if (!rect) return

    const { clientX, clientY } = event
    const isOutside = clientX < rect.left || clientX > rect.right ||
      clientY < rect.top || clientY > rect.bottom

    if (isOutside) {
      setIsDragging(false)
    }
  }

  const handleDrop = (event) => {
    event.preventDefault()
    event.stopPropagation()

    setIsDragging(false)

    if (!agentAcceptsFiles || !canRespond() || immediateThinking || isProcessing) return

    const files = event.dataTransfer.files
    if (files.length > 0) {
      handleFileUpload(files[0])
    }
  }

  const handleSubmitResponse = useCallback(async () => {
    if (!response.trim() || !displayTicket || immediateThinking || isProcessing) return

    const responseText = response.trim()
    const messageTimestamp = new Date().toISOString()
    setActiveFormSpec(null) // hide any open form overlay
    setActiveQuickReplies(null) // hide any quick reply chips
    setToastDismissed(true)
    pausePolling('message submission')
    setPendingMessage({ role: 'user', content: responseText, timestamp: messageTimestamp, pending: true })
    setResponse('')
    setLastAgentMessageCount(displayMessages.filter(m => m.role === 'assistant' && !isSystemInfoMessage(m)).length)

    await openStream({
      body: { response: responseText, user_email: user.email },
      onOpen: () => injectOptimisticMessage({ role: 'user', content: responseText, timestamp: messageTimestamp }),
    })
  }, [response, displayTicket, immediateThinking, isProcessing, setActiveFormSpec, pausePolling,
    injectOptimisticMessage, setPendingMessage, setResponse, setLastAgentMessageCount, displayMessages,
    isSystemInfoMessage, openStream, user])

  const handleFormFieldChange = useCallback((formId, fieldId, value) => {
    setFormDataByFormId(prev => ({
      ...prev,
      [formId]: {
        ...(prev[formId] || {}),
        [fieldId]: value
      }
    }))
  }, [])

  const handleFormSubmit = useCallback(async (formResponse) => {
    if (!displayTicket || immediateThinking || isProcessing) return

    const capturedFormSpec = formResponse.form_spec
    setActiveFormSpec(null)

    // Clear saved form data after successful submission
    if (formResponse.form_id) {
      setFormDataByFormId(prev => {
        const updated = { ...prev }
        delete updated[formResponse.form_id]
        return updated
      })
    }

    // Build the submitted_form_data block that the agent receives
    const responseText = `\`\`\`submitted_form_data\n${JSON.stringify({
      form_spec: capturedFormSpec,
      values: formResponse.values
    }, null, 2)}\n\`\`\``

    const formMessageTimestamp = new Date().toISOString()
    pausePolling('form submission')
    setPendingMessage({ role: 'user', content: responseText, timestamp: formMessageTimestamp, pending: true })
    setLastAgentMessageCount(displayMessages.filter(m => m.role === 'assistant' && !isSystemInfoMessage(m)).length)

    await openStream({
      body: { response: responseText, user_email: user.email },
      defaultErrorMessage: 'Failed to submit form. Please try again.',
      onOpen: () => injectOptimisticMessage({ role: 'user', content: responseText, timestamp: formMessageTimestamp }),
    })
  }, [displayTicket, immediateThinking, isProcessing, setActiveFormSpec, setFormDataByFormId,
    pausePolling, injectOptimisticMessage, setPendingMessage, setLastAgentMessageCount, displayMessages,
    isSystemInfoMessage, openStream, user])

  const handleQuickReplyClick = useCallback(async (text) => {
    if (!displayTicket || immediateThinking || isProcessing) return
    const messageTimestamp = new Date().toISOString()
    // Capture the pills before hiding them so a failed send can restore them —
    // clicking a pill removes the user's only recourse, and the optimistic error
    // bubble can be lost to a streaming race; bringing the pills back guarantees retry.
    const previousQuickReplies = activeQuickReplies
    setActiveQuickReplies(null)
    setActiveFormSpec(null)
    setToastDismissed(true)
    pausePolling('quick reply')
    setPendingMessage({ role: 'user', content: text, timestamp: messageTimestamp, pending: true })
    setResponse('')
    setLastAgentMessageCount(displayMessages.filter(m => m.role === 'assistant' && !isSystemInfoMessage(m)).length)
    await openStream({
      body: { response: text, user_email: user.email },
      onOpen: () => injectOptimisticMessage({ role: 'user', content: text, timestamp: messageTimestamp }),
      onError: () => setActiveQuickReplies(previousQuickReplies),
    })
  }, [displayTicket, immediateThinking, isProcessing, activeQuickReplies, setActiveQuickReplies, setActiveFormSpec,
    pausePolling, setPendingMessage, setResponse, setLastAgentMessageCount, displayMessages,
    isSystemInfoMessage, openStream, user, injectOptimisticMessage])

  const handleRetryMessage = useCallback(async () => {
    if (!pendingMessage?.error || !displayTicket) return

    setActiveFormSpec(null) // close any open form overlay before retrying

    if (pendingMessage.file) {
      setPendingMessage(null)
      await handleFileUpload(pendingMessage.file)
      return
    }

    setPendingMessage(prev => prev ? { ...prev, pending: true, error: false, errorMessage: undefined } : null)
    pausePolling('message retry')
    setLastAgentMessageCount(displayMessages.filter(m => m.role === 'assistant' && !isSystemInfoMessage(m)).length)

    await openStream({ body: { response: pendingMessage.content, user_email: user.email } })
  }, [pendingMessage, displayTicket, setActiveFormSpec, setPendingMessage, pausePolling,
    setLastAgentMessageCount, displayMessages, isSystemInfoMessage, openStream, user, handleFileUpload])

  const handleDeleteTicket = async () => {
    if (!ticket) return

    setDeleting(true)
    try {
      await ticketsAPI.deleteTicket(ticket.id)
      setDeleteDialogOpen(false)

      // Immediately refresh tickets and navigate to root
      onTicketUpdated?.('deleted')

      // Navigate to root tickets page to avoid "ticket not found" error
      navigate('/')
    } catch (error) {
      console.error('Failed to delete ticket:', error)
      // TODO: Show error notification
    } finally {
      setDeleting(false)
    }
  }

  const handleArchiveTicket = async () => {
    if (!ticket) return

    setArchiving(true)
    try {
      await ticketsAPI.archiveTicket(ticket.id)
      setArchiveDialogOpen(false)

      // Immediately refresh tickets and navigate to root
      onTicketUpdated?.('archived')

      // Navigate to root tickets page
      navigate('/')
    } catch (error) {
      console.error('Failed to archive ticket:', error)
      // TODO: Show error notification
    } finally {
      setArchiving(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmitResponse()
    }
  }

  // Notes functionality
  const handleOpenNotesModal = (messageIndex) => {
    // Store both the merged index and the message for child ticket detection
    setSelectedMessageIndex(messageIndex)
    setNoteText('')
    setNotesModalOpen(true)
  }

  const handleCloseNotesModal = () => {
    setNotesModalOpen(false)
    setSelectedMessageIndex(null)
    setNoteText('')
  }

  const handleSaveNote = async () => {
    if (!ticket || selectedMessageIndex === null || !noteText.trim()) return

    setSavingNote(true)
    try {
      const selectedMessage = displayMessages[selectedMessageIndex]
      const isChildMessage = selectedMessage?.messageMetadata?.tags?.includes('is_child')

      let targetTicketId = ticket.id
      let messageIndex = selectedMessageIndex

      if (isChildMessage) {
        // Extract child ticket type from tags
        const tags = selectedMessage.messageMetadata?.tags?.split(',').map(t => t.trim()) || []
        const childTicketType = tags.find(t =>
          t !== 'is_child' &&
          !['Memory', 'Knowledge', 'API', 'api', 'EmailAction', 'Handover', 'Complete', 'AgentHandback', 'SubmittedForm'].includes(t)
        )

        // Find the child ticket with matching type
        const childTicket = ticket.child_tickets?.find(child =>
          child.ticket_type === childTicketType || child.type === childTicketType
        )

        if (childTicket) {
          targetTicketId = childTicket.id

          // Calculate message index within child's conversation
          // Count how many messages from THIS child appear before and including this one
          const childMessagesBefore = displayMessages
            .slice(0, selectedMessageIndex + 1)
            .filter(msg => {
              const msgTags = msg.messageMetadata?.tags?.split(',').map(t => t.trim()) || []
              return msgTags.includes('is_child') && msgTags.includes(childTicketType)
            })
          messageIndex = childMessagesBefore.length - 1
        } else {
          console.error('Could not find child ticket for type:', childTicketType)
          return
        }
      } else {
        // Parent message - count NON-child messages before this one
        messageIndex = displayMessages
          .slice(0, selectedMessageIndex + 1)
          .filter(msg => !msg.messageMetadata?.tags?.includes('is_child'))
          .length - 1
      }

      console.log('[Notes] Saving note:', {
        mergedIndex: selectedMessageIndex,
        isChild: isChildMessage,
        targetTicketId,
        messageIndex
      })

      const newNote = await ticketsAPI.addTicketNote(targetTicketId, messageIndex, noteText.trim())
      // Add the new note to our local state with source_ticket_id
      setNotesData(prev => [...prev, { ...newNote, source_ticket_id: targetTicketId }])
      setNoteText('')
      // Close the modal after saving
      handleCloseNotesModal()
    } catch (error) {
      console.error('Failed to save note:', error)
      // TODO: Show error notification
    } finally {
      setSavingNote(false)
    }
  }

  // Handler for saving notes from form (doesn't use modal state)
  const handleSaveFormNote = useCallback(async (messageIndex, noteContent) => {
    if (!ticket || messageIndex === null || !noteContent.trim()) return

    try {
      const selectedMessage = displayMessages[messageIndex]
      const isChildMessage = selectedMessage?.messageMetadata?.tags?.includes('is_child')

      let targetTicketId = ticket.id
      let msgIndex = messageIndex

      if (isChildMessage) {
        // Extract child ticket type from tags
        const tags = selectedMessage.messageMetadata?.tags?.split(',').map(t => t.trim()) || []
        const childTicketType = tags.find(t =>
          t !== 'is_child' &&
          !['Memory', 'Knowledge', 'API', 'api', 'EmailAction', 'Handover', 'Complete', 'AgentHandback', 'SubmittedForm'].includes(t)
        )

        // Find the child ticket with matching type
        const childTicket = ticket.child_tickets?.find(child =>
          child.ticket_type === childTicketType || child.type === childTicketType
        )

        if (childTicket) {
          targetTicketId = childTicket.id

          // Calculate message index within child's conversation
          const childMessagesBefore = displayMessages
            .slice(0, messageIndex + 1)
            .filter(msg => {
              const msgTags = msg.messageMetadata?.tags?.split(',').map(t => t.trim()) || []
              return msgTags.includes('is_child') && msgTags.includes(childTicketType)
            })
          msgIndex = childMessagesBefore.length - 1
        } else {
          console.error('Could not find child ticket for type:', childTicketType)
          return
        }
      } else {
        // Parent message - count NON-child messages before this one
        msgIndex = displayMessages
          .slice(0, messageIndex + 1)
          .filter(msg => !msg.messageMetadata?.tags?.includes('is_child'))
          .length - 1
      }

      const newNote = await ticketsAPI.addTicketNote(targetTicketId, msgIndex, noteContent.trim())
      // Add the new note to our local state with source_ticket_id
      setNotesData(prev => [...prev, { ...newNote, source_ticket_id: targetTicketId }])
    } catch (error) {
      console.error('Failed to save form note:', error)
      throw error // Re-throw so FormOverlay can handle it
    }
  }, [ticket, displayMessages])

  const getNotesForMessage = (messageIndex) => {
    const message = displayMessages[messageIndex]
    const isChildMessage = message?.messageMetadata?.tags?.includes('is_child')

    let targetTicketId = ticket?.id
    let messageIdx = messageIndex

    if (isChildMessage) {
      // Extract child ticket type from tags
      const tags = message.messageMetadata?.tags?.split(',').map(t => t.trim()) || []
      const childTicketType = tags.find(t =>
        t !== 'is_child' &&
        !['Memory', 'Knowledge', 'API', 'api', 'EmailAction', 'Handover', 'Complete', 'AgentHandback', 'SubmittedForm'].includes(t)
      )

      // Find the child ticket with matching type
      const childTicket = ticket?.child_tickets?.find(child =>
        child.ticket_type === childTicketType || child.type === childTicketType
      )

      if (childTicket) {
        targetTicketId = childTicket.id

        // Calculate message index within child's conversation
        const childMessagesBefore = displayMessages
          .slice(0, messageIndex + 1)
          .filter(msg => {
            const msgTags = msg.messageMetadata?.tags?.split(',').map(t => t.trim()) || []
            return msgTags.includes('is_child') && msgTags.includes(childTicketType)
          })
        messageIdx = childMessagesBefore.length - 1
      } else {
        return []
      }
    } else {
      // Parent message - count NON-child messages before this one
      messageIdx = displayMessages
        .slice(0, messageIndex + 1)
        .filter(msg => !msg.messageMetadata?.tags?.includes('is_child'))
        .length - 1
    }

    return notesData.filter(note =>
      note.source_ticket_id === targetTicketId && note.message_index === messageIdx
    )
  }

  const hasNotes = (messageIndex) => {
    const message = displayMessages[messageIndex]
    const isChildMessage = message?.messageMetadata?.tags?.includes('is_child')

    let targetTicketId = ticket?.id
    let messageIdx = messageIndex

    if (isChildMessage) {
      // Extract child ticket type from tags
      const tags = message.messageMetadata?.tags?.split(',').map(t => t.trim()) || []
      const childTicketType = tags.find(t =>
        t !== 'is_child' &&
        !['Memory', 'Knowledge', 'API', 'api', 'EmailAction', 'Handover', 'Complete', 'AgentHandback', 'SubmittedForm'].includes(t)
      )

      // Find the child ticket with matching type
      const childTicket = ticket?.child_tickets?.find(child =>
        child.ticket_type === childTicketType || child.type === childTicketType
      )

      if (childTicket) {
        targetTicketId = childTicket.id

        // Calculate message index within child's conversation
        const childMessagesBefore = displayMessages
          .slice(0, messageIndex + 1)
          .filter(msg => {
            const msgTags = msg.messageMetadata?.tags?.split(',').map(t => t.trim()) || []
            return msgTags.includes('is_child') && msgTags.includes(childTicketType)
          })
        messageIdx = childMessagesBefore.length - 1
      } else {
        return false
      }
    } else {
      // Parent message - count NON-child messages before this one
      messageIdx = displayMessages
        .slice(0, messageIndex + 1)
        .filter(msg => !msg.messageMetadata?.tags?.includes('is_child'))
        .length - 1
    }

    return notesData.some(note =>
      note.source_ticket_id === targetTicketId && note.message_index === messageIdx
    )
  }

  // Toggle system info visibility
  const toggleSystemInfo = () => {
    const newValue = !showSystemInfo
    setShowSystemInfo(newValue)
    try {
      localStorage.setItem('agentflow-show-system-info', JSON.stringify(newValue))
    } catch (error) {
      console.warn('Failed to save system info preference:', error)
    }
  }

  // Extract JSON keys from memory/cache content for preview
  const extractMemoryKeys = (content) => {
    try {
      // Look for JSON content in the message
      const jsonMatch = content.match(/\{[\s\S]*\}/)
      if (jsonMatch) {
        const jsonContent = JSON.parse(jsonMatch[0])
        const keys = Object.keys(jsonContent)
        return keys.length > 0 ? keys.slice(0, 3).join(', ') + (keys.length > 3 ? '...' : '') : 'data'
      }

      // Fallback: look for key-value patterns
      const keyMatches = content.match(/["']([^"']+)["']\s*:/g)
      if (keyMatches) {
        const keys = keyMatches.slice(0, 3).map(match => match.replace(/["':\s]/g, ''))
        return keys.join(', ') + (keyMatches.length > 3 ? '...' : '')
      }

      return 'cache data'
    } catch (error) {
      return 'cache data'
    }
  }

  // Helper to clean internal metadata markers from output text
  const cleanOutputText = (text) => {
    if (!text) return text
    // Remove internal metadata markers like #[uuid-string]
    return text.replace(/#\[[^\]]+\]/g, '').trim()
  }

  // Helper to get outputs array (handles both array and legacy string formats)
  const getOutputsArray = () => {
    if (!displayTicket?.last_output) return []

    // Handle array (new format)
    if (Array.isArray(displayTicket.last_output)) {
      return displayTicket.last_output.map(output => cleanOutputText(output))
    }

    // Handle legacy string format
    return displayTicket.last_output ? [cleanOutputText(displayTicket.last_output)] : []
  }

  // During streaming, use live SSE outputs; otherwise fall back to polled ticket data
  const getDisplayOutputs = () => streamingOutputs.length > 0 ? streamingOutputs : getOutputsArray()


  // Show loading when explicitly loading OR when ticket doesn't match current ticketId
  // (handles race condition where ticketId changes before effect runs)
  if (loading || (ticketId && ticket && ticket.id !== ticketId)) {
    return <LoadingSpinner message="Loading ticket" agentStyle={false} />
  }

  if (error) {
    return (
      <ErrorAlert
        message="Failed to load conversation"
        error={error}
        onRetry={onRefresh}
      />
    )
  }

  if (!ticket) {
    if (embedMode) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            textAlign: 'center',
            p: 3,
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Unable to load ticket
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            There was a problem retrieving this ticket. Please try again.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => window.location.reload()}
          >
            Reload
          </Button>
        </Box>
      )
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          textAlign: 'center',
          p: 3,
        }}
      >
        <Assignment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Welcome to AgentFlow
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select a ticket from the list to view its conversation
        </Typography>
      </Box>
    )
  }

  // Render execution logs view
  const renderLogsView = () => {
    const logs = ticket.logs || []

    if (logs.length === 0) {
      return (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Code sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No execution logs yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Agent execution logs will appear here as the ticket is processed
            </Typography>
          </CardContent>
        </Card>
      )
    }

    return (
      <Box sx={{ maxWidth: 1000, mx: 'auto', px: 2 }}>
        {logs.map((log, index) => (
          <Card key={index} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  {log.function_name || log.log_type || 'System'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(log.timestamp)}
                </Typography>
              </Box>

              {/* Message Preview */}
              {log.data?.message_preview && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Message Preview:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      bgcolor: 'blue.50',
                      p: 1,
                      borderRadius: 1,
                      fontStyle: 'italic',
                      borderLeft: 3,
                      borderColor: 'blue.300'
                    }}
                  >
                    {log.data.message_preview}
                  </Typography>
                </Box>
              )}

              {/* Result Preview */}
              {log.data?.result_preview && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Result Preview:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      bgcolor: 'green.50',
                      p: 1,
                      borderRadius: 1,
                      fontStyle: 'italic',
                      borderLeft: 3,
                      borderColor: 'green.300'
                    }}
                  >
                    {log.data.result_preview}
                  </Typography>
                </Box>
              )}

              {log.parameters && Object.keys(log.parameters).length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Parameters:
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      fontSize: '0.75rem',
                      bgcolor: 'grey.100',
                      p: 1,
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: '200px'
                    }}
                  >
                    {JSON.stringify(log.parameters, null, 2)}
                  </Box>
                </Box>
              )}

              {log.result && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Result:
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      fontSize: '0.75rem',
                      bgcolor: 'grey.50',
                      p: 1,
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: '300px',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {typeof log.result === 'string' ? log.result : JSON.stringify(log.result, null, 2)}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
        <div ref={messagesEndRef} data-component="messages-end-marker" />
      </Box>
    )
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {/* Header - hidden in embed mode */}
      {!embedMode && (
        <Paper
          elevation={1}
          sx={{
            borderRadius: 0,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          {/* Compact header for mobile, full header for desktop */}
          {isMobile ? (
            <Box>
              {/* Mobile compact header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Ticket #{ticket.id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {ticket.type || ticket.ticket_type || 'Unknown'}
                    </Typography>
                  </Box>
                  <Chip
                    label={ticket.status}
                    {...(typeof getStatusColor(ticket.status, theme) === 'object'
                      ? { sx: getStatusColor(ticket.status, theme) }
                      : { color: getStatusColor(ticket.status, theme) })}
                    size="small"
                  />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {/* Hidden toggle - keeping viewMode state for potential future use
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(event, newMode) => newMode && setViewMode(newMode)}
                  size="small"
                >
                  <ToggleButton value="conversation" aria-label="conversation view">
                    <Chat fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="logs" aria-label="logs view">
                    <Code fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
                */}

                  <IconButton
                    size="small"
                    onClick={() => setHeaderExpanded(!headerExpanded)}
                    aria-label="toggle ticket details"
                  >
                    <InfoOutlined fontSize="small" />
                    {headerExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </IconButton>

                  {canArchive() && (
                    <IconButton
                      size="small"
                      color="warning"
                      onClick={() => setArchiveDialogOpen(true)}
                      aria-label="archive ticket"
                    >
                      <Archive fontSize="small" />
                    </IconButton>
                  )}

                  {canPermanentlyDelete() && !readOnly && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteDialogOpen(true)}
                      aria-label="permanently delete ticket"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>

              {/* Collapsible details for mobile */}
              <Collapse in={headerExpanded}>
                <Box sx={{ px: 2, pb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {ticket.type || ticket.ticket_type || 'Unknown'}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Assigned To
                      </Typography>
                      <Typography variant="body2">
                        {ticket.assigned_to || 'Unassigned'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Created
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(ticket.created_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Created By
                      </Typography>
                      <Typography variant="body2">
                        {ticket.created_by || 'System'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Last Updated
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(ticket.modified_at)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>
            </Box>
          ) : (
            /* Desktop full header */
            <Box sx={{ p: 2, maxHeight: 72 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%' }}>
                {/* Left: Ticket Number and Type */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box>
                    <Typography variant="h6">
                      Ticket #{ticket.id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {ticket.type || ticket.ticket_type || 'Unknown'}
                    </Typography>
                  </Box>
                  <Chip
                    label={ticket.status === 'waiting' ? `waiting for ${ticket.role}` : ticket.status}
                    {...(typeof getStatusColor(ticket.status === 'waiting' ? `waiting for ${ticket.role}` : ticket.status, theme) === 'object'
                      ? { sx: getStatusColor(ticket.status === 'waiting' ? `waiting for ${ticket.role}` : ticket.status, theme) }
                      : { color: getStatusColor(ticket.status === 'waiting' ? `waiting for ${ticket.role}` : ticket.status, theme) })}
                    size="small"
                  />
                </Box>

                {/* Center: Compact Ticket Details Card */}
                <Card
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: 'transparent',
                    minWidth: 400,
                    mx: 2,
                    border: 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-around', gap: 2 }}>
                    <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Assigned To
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                        {ticket.assigned_to?.split('@')[0] || 'Unassigned'}
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ textAlign: 'center', minWidth: 60 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Created
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                        {formatDate(ticket.created_at)}
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Created By
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                        {ticket.created_by?.split('@')[0] || 'System'}
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ textAlign: 'center', minWidth: 70 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Updated
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                        {formatDate(ticket.modified_at)}
                      </Typography>
                    </Box>
                  </Box>
                </Card>

                {/* Right: Archive/Delete button */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title={showInfoPanel ? 'Hide info panel' : 'Show info panel'}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const next = !showInfoPanel
                        setShowInfoPanel(next)
                        localStorage.setItem('agentflow-show-info-panel', JSON.stringify(next))
                      }}
                      color={showInfoPanel ? 'primary' : 'default'}
                      aria-label="toggle info panel"
                    >
                      <DataObject fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {/* Hidden toggle - keeping viewMode state for potential future use
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(event, newMode) => newMode && setViewMode(newMode)}
                  size="small"
                >
                  <ToggleButton value="conversation" aria-label="conversation view">
                    <Chat fontSize="small" />
                    <Typography variant="caption" sx={{ ml: 0.5, display: { xs: 'none', sm: 'block' } }}>
                      Chat
                    </Typography>
                  </ToggleButton>
                  <ToggleButton value="logs" aria-label="logs view">
                    <Code fontSize="small" />
                    <Typography variant="caption" sx={{ ml: 0.5, display: { xs: 'none', sm: 'block' } }}>
                      Logs
                    </Typography>
                  </ToggleButton>
                </ToggleButtonGroup>
                */}

                  {canArchive() && (
                    <IconButton
                      color="warning"
                      onClick={() => setArchiveDialogOpen(true)}
                      aria-label="archive ticket"
                    >
                      <Archive />
                    </IconButton>
                  )}

                  {canPermanentlyDelete() && !readOnly && (
                    <IconButton
                      color="error"
                      onClick={() => setDeleteDialogOpen(true)}
                      aria-label="permanently delete ticket"
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Paper>
      )}
      <Box sx={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', overflow: 'hidden', paddingRight: showInfoPanel && !isMobile && !isPanelOverlay ? '340px' : 0, transition: 'padding-right 0.2s ease' }}>
      {/* No tabs - always auto-show active child or parent */}

      {/* Archived Indicator Banner - hidden in embed mode */}
      {!embedMode && ticket?.archived && (
        <Alert
          severity="warning"
          icon={<Archive />}
          sx={{
            borderRadius: 0,
            borderTop: 'none',
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'warning.light',
            color: 'warning.dark',
            fontWeight: 600,
            '& .MuiAlert-icon': {
              color: 'warning.dark',
            },
          }}
        >
          This ticket is archived
          {ticket.archived_at && ` • Archived ${formatDate(ticket.archived_at)}`}
        </Alert>
      )}

      {activeToast && !toastDismissed && (
        <ConversationBanner
          toast={activeToast}
          onDismiss={() => setToastDismissed(true)}
        />
      )}

      {/* Content Area - Conversation or Logs */}
      <Box
        ref={conversationContainerRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-component="conversation-container"
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          p: 2,
          bgcolor: readOnly ? '#ffffff' : (theme.palette.mode === 'dark' ? '#1e2229' : '#f5f5f5'),
          position: 'relative',
        }}
      >
        {/* Wrapper for content with mask overlay */}
        <Box sx={{ position: 'relative', minHeight: '100%' }} data-component="content-wrapper">
          {/* Backdrop mask when form is expanded */}
          {parsedFormSpec && formExpanded && (
            <Box
              data-component="message-pane-mask"
              sx={{
                position: 'absolute',
                top: -16,
                left: -16,
                right: -16,
                bottom: -16,
                bgcolor: 'rgba(0, 0, 0, 0.15)',
                zIndex: 1,
                transition: 'all 0.3s ease-in-out',
                pointerEvents: 'none',
              }}
            />
          )}
          {viewMode === 'conversation' ? (
            /* Conversation View */
            loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <LoadingSpinner message="Loading ticket" agentStyle={false} />
              </Box>
            ) : allMessages.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                {isProcessing && ticket?.status === 'processing' && ticket?.role === 'agent' ? (
                  <>
                    {/* Show "Working on your request" only if no outputs yet */}
                    {getDisplayOutputs().length === 0 && (
                      <>
                        <Typography
                          variant="h6"
                          color="secondary.main"
                          gutterBottom
                          sx={{
                            fontWeight: 400,
                            letterSpacing: '0.1em',
                            fontSize: '1.1rem'
                          }}
                        >
                          Working on your request
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontWeight: 400,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            fontSize: '0.9rem'
                          }}
                          gutterBottom
                        >
                          {streamingAction || displayTicket?.current_action || `Izzi is ${thinkingPhrase || 'thinking'}`}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.3, mt: 1 }}>
                          {[0, 1, 2].map((i) => (
                            <Box
                              key={i}
                              sx={{
                                width: 8,
                                height: 8,
                                backgroundColor: 'text.secondary',
                                borderRadius: '50%',
                                animation: `typingDot 1.4s infinite ease-in-out`,
                                animationDelay: `${i * 0.16}s`,
                                '@keyframes typingDot': {
                                  '0%, 80%, 100%': {
                                    transform: 'scale(0.8)',
                                    opacity: 0.5,
                                  },
                                  '40%': {
                                    transform: 'scale(1)',
                                    opacity: 1,
                                  },
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </>
                    )}

                    {/* Show all last_output items stacked vertically */}
                    {getDisplayOutputs().length > 0 && (
                      <Box sx={{
                        mt: 3,
                        maxWidth: 800,
                        mx: 'auto',
                        textAlign: 'left',
                      }}>
                        {getDisplayOutputs().map((output, index) => (
                          <Box
                            key={index}
                            sx={{
                              mb: 1.5,
                              animation: 'fadeInOutput 0.8s ease-in-out, pulseOutput 2s ease-in-out infinite',
                              animationDelay: `${index * 0.1}s, ${0.8 + index * 0.1}s`,
                              '@keyframes fadeInOutput': {
                                '0%': {
                                  opacity: 0,
                                  transform: 'translateY(-10px)',
                                },
                                '100%': {
                                  opacity: 1.0,
                                  transform: 'translateY(0)',
                                },
                              },
                              '@keyframes pulseOutput': {
                                '0%, 100%': {
                                  opacity: 1.0,
                                },
                                '50%': {
                                  opacity: 0.15,
                                },
                              },
                            }}
                          >
                            <Typography
                              variant="caption"
                              component="div"
                              sx={{
                                color: 'text.secondary',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                              }}
                            >
                              <MessageContent
                                content={output}
                                isUserMessage={false}
                                onFormSubmit={handleFormSubmit}
                                formDataByFormId={formDataByFormId}
                                onFormFieldChange={handleFormFieldChange}
                              />
                            </Typography>
                          </Box>
                        ))}

                        {/* Show current_action below outputs with typing dots */}
                        {displayTicket?.current_action && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, justifyContent: 'flex-start' }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.9rem' }}>
                              {displayTicket.current_action}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.3 }}>
                              {[0, 1, 2].map((i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    backgroundColor: 'text.secondary',
                                    borderRadius: '50%',
                                    animation: `typingDot 1.4s infinite ease-in-out`,
                                    animationDelay: `${i * 0.16}s`,
                                    '@keyframes typingDot': {
                                      '0%, 80%, 100%': {
                                        transform: 'scale(0.8)',
                                        opacity: 0.5,
                                      },
                                      '40%': {
                                        transform: 'scale(1)',
                                        opacity: 1,
                                      },
                                    },
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                    <LoadingSpinner message="Loading ticket" agentStyle={false} />
                  </Box>
                )}
                <div ref={messagesEndRef} data-component="messages-end-marker" />
              </Box>
            ) : (
              <Box sx={{
                maxWidth: { xs: '100%', sm: 700, md: 800, lg: 900 },
                mx: 'auto',
                px: { xs: 1, sm: 2 },
                width: '100%',
                overflow: 'hidden' // Prevent horizontal overflow
              }}>
                {/* Translate controls */}
                {(() => {
                  // Build a lookup map for translated content by message id
                  const translationMap = translatedMessages
                    ? Object.fromEntries(translatedMessages.map(m => [m.id, m.content]))
                    : null
                  // Find the last visible message index (skip hidden messages like Memory cache misses and EmailActions when system info is off)
                  const lastVisibleIndex = allMessages.reduce((last, msg, i) => {
                    const isMemoryCacheMiss = msg.messageMetadata?.tags?.includes('Memory') && msg.content?.includes('Cache Miss:');
                    const isHiddenEmail = msg.messageMetadata?.tags?.includes('EmailAction') && !showSystemInfo;
                    return (!isMemoryCacheMiss && !isHiddenEmail) ? i : last;
                  }, -1);
                  return allMessages.map((message, index) => {
                  const displayMessage = translationMap && message.id && translationMap[message.id] !== undefined
                    ? { ...message, content: translationMap[message.id] }
                    : message
                  return (
                  <ConversationMessage
                    key={index}  // Use index to avoid duplicate key warnings
                    message={displayMessage}
                    index={message.originalIndex}  // Use original index for note matching
                    allMessages={allMessages}
                    isExpanded={expandedMessages.has(index)}
                    toggleMessageExpansion={toggleMessageExpansion}
                    formatDate={formatDate}
                    handleFormSubmit={handleFormSubmit}
                    formDataByFormId={formDataByFormId}
                    handleFormFieldChange={handleFormFieldChange}
                    user={user}
                    theme={theme}
                    extractMemoryKeys={extractMemoryKeys}
                    hasApiTag={hasApiTag}
                    canAddNotes={canAddNotes}
                    hasNotes={hasNotes(message.originalIndex)}  // Use original index for note matching
                    hoveredMessageIndex={hoveredMessageIndex}
                    setHoveredMessageIndex={setHoveredMessageIndex}
                    handleOpenNotesModal={handleOpenNotesModal}
                    parsedFormSpec={parsedFormSpec}
                    showSystemInfo={showSystemInfo}
                    ticket={displayTicket}
                    parentTicket={ticket}
                    expandedForms={expandedForms}
                    setExpandedForms={setExpandedForms}
                    isLastVisibleMessage={index === lastVisibleIndex}
                    onRetryMessage={handleRetryMessage}
                  />
                )
                });
                })()}

                {/* Translate / Show original buttons - only show when languages differ */}
                {(user?.preferred_language || 'en') !== (ticket?.data?.preferred_language || 'en') && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={isTranslating}
                    onClick={handleTranslate}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                  >
                    {isTranslating ? 'Translating...' : 'Translate'}
                  </Button>
                  {translatedMessages && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setTranslatedMessages(null)}
                      sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                    >
                      Show original
                    </Button>
                  )}
                </Box>
                )}

                {/* Show file uploading indicator */}
                {isUploadingFile && (
                  <Box
                    sx={{
                      mb: 2,
                      display: 'flex',
                      flexDirection: 'row-reverse',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: 'primary.main',
                        width: 32,
                        height: 32,
                        mx: 1,
                        mt: 0.5,
                      }}
                    >
                      <Person />
                    </Avatar>
                    <Box
                      sx={{
                        maxWidth: { xs: '85%', sm: '75%', md: '70%' },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          mb: 0.5,
                          gap: 1,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                          {user?.name || user?.email || 'You'}
                        </Typography>
                      </Box>
                      <Card
                        sx={{
                          backgroundColor: (theme) => theme.palette.primary.main,
                          color: 'primary.contrastText',
                          borderRadius: 2,
                          boxShadow: 1,
                          animation: 'pulse 1.5s ease-in-out infinite',
                          '@keyframes pulse': {
                            '0%': { opacity: 1 },
                            '50%': { opacity: 0.7 },
                            '100%': { opacity: 1 },
                          },
                        }}
                      >
                        <CardContent sx={{
                          p: 2,
                          '&:last-child': { pb: 2 },
                          wordWrap: 'break-word',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">
                              Uploading file...
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.3 }}>
                              {[0, 1, 2].map((i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    backgroundColor: 'primary.contrastText',
                                    borderRadius: '50%',
                                    animation: `typingDot 1.4s infinite ease-in-out`,
                                    animationDelay: `${i * 0.16}s`,
                                    '@keyframes typingDot': {
                                      '0%, 80%, 100%': {
                                        transform: 'scale(0.8)',
                                        opacity: 0.5,
                                      },
                                      '40%': {
                                        transform: 'scale(1)',
                                        opacity: 1,
                                      },
                                    },
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  </Box>
                )}

                {/* Show streaming message as it arrives (submitting user direct stream) */}
                {directStreamContent && (
                  <ConversationMessage
                    message={{
                      role: 'assistant',
                      content: directStreamContent,
                      timestamp: new Date().toISOString(),
                      streaming: true,
                    }}
                    index={0}
                    allMessages={[]}
                    isExpanded={false}
                    toggleMessageExpansion={() => {}}
                    formatDate={formatDate}
                    handleFormSubmit={handleFormSubmit}
                    formDataByFormId={formDataByFormId}
                    handleFormFieldChange={handleFormFieldChange}
                    user={user}
                    theme={theme}
                    extractMemoryKeys={extractMemoryKeys}
                    hasApiTag={hasApiTag}
                    canAddNotes={false}
                    hasNotes={false}
                    hoveredMessageIndex={null}
                    setHoveredMessageIndex={() => {}}
                    handleOpenNotesModal={() => {}}
                    parsedFormSpec={parsedFormSpec}
                    showSystemInfo={showSystemInfo}
                    ticket={displayTicket}
                    parentTicket={ticket}
                    expandedForms={expandedForms}
                    setExpandedForms={setExpandedForms}
                    isLastVisibleMessage={true}
                    onRetryMessage={handleRetryMessage}
                  />
                )}

                {/* Minimal thinking dots shown while typewriter is streaming — signals agent is still working */}
                {immediateThinking && directStreamContent && (
                  <Box sx={{ display: 'flex', gap: 0.3, px: 2, pb: 1 }}>
                    {[0, 1, 2].map((i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 6,
                          height: 6,
                          backgroundColor: 'text.secondary',
                          borderRadius: '50%',
                          animation: `typingDot 1.4s infinite ease-in-out`,
                          animationDelay: `${i * 0.16}s`,
                        }}
                      />
                    ))}
                  </Box>
                )}

                {/* Show immediate thinking indicator right after user submits response, and keep it visible throughout streaming */}
                {immediateThinking && !directStreamContent && (
                  <Box
                    sx={{
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: 'transparent',
                        color: 'text.primary',
                        borderRadius: 0,
                        boxShadow: 'none',
                        p: 2,
                        animation: 'pulse 1.5s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%': { opacity: 1 },
                          '50%': { opacity: 0.7 },
                          '100%': { opacity: 1 },
                        },
                      }}
                    >
                      {/* Show current_action if available, otherwise show random thinking phrase */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.9rem' }}>
                          {streamingAction || displayTicket?.current_action || thinkingPhrase || 'thinking'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.3 }}>
                          {[0, 1, 2].map((i) => (
                            <Box
                              key={i}
                              sx={{
                                width: 8,
                                height: 8,
                                backgroundColor: 'text.secondary',
                                borderRadius: '50%',
                                animation: `typingDot 1.4s infinite ease-in-out`,
                                animationDelay: `${i * 0.16}s`,
                                '@keyframes typingDot': {
                                  '0%, 80%, 100%': {
                                    transform: 'scale(0.8)',
                                    opacity: 0.5,
                                  },
                                  '40%': {
                                    transform: 'scale(1)',
                                    opacity: 1,
                                  },
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </Box>

                      {/* Show all last_output items stacked vertically */}
                      {getDisplayOutputs().length > 0 && (
                        <Box sx={{ mt: 1.5, textAlign: 'left' }}>
                          {getDisplayOutputs().map((output, index) => (
                            <Box
                              key={index}
                              sx={{
                                mb: 1,
                                animation: 'fadeInOutput 0.8s ease-in-out, pulseOutput 2s ease-in-out infinite',
                                animationDelay: `${index * 0.1}s, ${0.8 + index * 0.1}s`,
                                '@keyframes fadeInOutput': {
                                  '0%': {
                                    opacity: 0,
                                    transform: 'translateY(-10px)',
                                  },
                                  '100%': {
                                    opacity: 1.0,
                                    transform: 'translateY(0)',
                                  },
                                },
                                '@keyframes pulseOutput': {
                                  '0%, 100%': {
                                    opacity: 1.0,
                                  },
                                  '50%': {
                                    opacity: 0.15,
                                  },
                                },
                              }}
                            >
                              <Typography
                                variant="caption"
                                component="div"
                                sx={{
                                  color: 'text.secondary',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  textAlign: 'left'
                                }}
                              >
                                <MessageContent
                                  content={output}
                                  isUserMessage={false}
                                  onFormSubmit={handleFormSubmit}
                                  formDataByFormId={formDataByFormId}
                                  onFormFieldChange={handleFormFieldChange}
                                />
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Show typing indicator if agent is processing and assigned to agent */}
                {ticket?.role === 'agent' && ticket?.status !== 'complete' && ticket?.status !== 'error' && !immediateThinking && !directStreamContent && (
                  <Box
                    ref={thinkingBubbleRef}
                    sx={{
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: 'transparent',
                        color: 'text.primary',
                        borderRadius: 0,
                        boxShadow: 'none',
                        p: 2,
                      }}
                    >
                      {/* Show current_action if available, otherwise show random thinking phrase */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.9rem' }}>
                          {streamingAction || displayTicket?.current_action || thinkingPhrase || 'thinking'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.3 }}>
                          {[0, 1, 2].map((i) => (
                            <Box
                              key={i}
                              sx={{
                                width: 8,
                                height: 8,
                                backgroundColor: 'text.secondary',
                                borderRadius: '50%',
                                animation: `typingDot 1.4s infinite ease-in-out`,
                                animationDelay: `${i * 0.16}s`,
                                '@keyframes typingDot': {
                                  '0%, 80%, 100%': {
                                    transform: 'scale(0.8)',
                                    opacity: 0.5,
                                  },
                                  '40%': {
                                    transform: 'scale(1)',
                                    opacity: 1,
                                  },
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </Box>

                      {/* Show all last_output items stacked vertically */}
                      {getDisplayOutputs().length > 0 && (
                        <Box sx={{ mt: 1.5, textAlign: 'left' }}>
                          {getDisplayOutputs().map((output, index) => (
                            <Box
                              key={index}
                              sx={{
                                mb: 1,
                                animation: 'fadeInOutput 0.8s ease-in-out, pulseOutput 2s ease-in-out infinite',
                                animationDelay: `${index * 0.1}s, ${0.8 + index * 0.1}s`,
                                '@keyframes fadeInOutput': {
                                  '0%': {
                                    opacity: 0,
                                    transform: 'translateY(-10px)',
                                  },
                                  '100%': {
                                    opacity: 1.0,
                                    transform: 'translateY(0)',
                                  },
                                },
                                '@keyframes pulseOutput': {
                                  '0%, 100%': {
                                    opacity: 1.0,
                                  },
                                  '50%': {
                                    opacity: 0.15,
                                  },
                                },
                              }}
                            >
                              <Typography
                                variant="caption"
                                component="div"
                                sx={{
                                  color: 'text.secondary',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  textAlign: 'left'
                                }}
                              >
                                <MessageContent
                                  content={output}
                                  isUserMessage={false}
                                  onFormSubmit={handleFormSubmit}
                                  formDataByFormId={formDataByFormId}
                                  onFormFieldChange={handleFormFieldChange}
                                />
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                <div ref={messagesEndRef} data-component="messages-end-marker" />
              </Box>
            )
          ) : (
            /* Logs View */
            renderLogsView()
          )}
        </Box>
      </Box>

      {/* Response Input */}
      {!readOnly && <Box
        data-component="input-card-container"
        sx={{
          px: 2,
          pb: 2,
          pt: 0,
          borderTop: 0,
          borderColor: 'divider',
          bgcolor: theme.palette.mode === 'dark' ? '#1e2229' : '#f5f5f5',
          position: 'relative',
          zIndex: 2,
          transition: 'all 0.3s ease-in-out',
        }}
      >
          {/* Mask overlay for input card when form is expanded */}
          {parsedFormSpec && formExpanded && (
            <Box
              data-component="input-card-mask"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(0, 0, 0, 0.15)',
                zIndex: 0,
                transition: 'all 0.3s ease-in-out',
                pointerEvents: 'none',
              }}
            />
          )}
          <Box sx={{ maxWidth: { xs: 600, sm: 700, md: 800, lg: 900 }, mx: 'auto', position: 'relative', zIndex: 1 }} data-component="form-and-input-wrapper">
            {/* Form overlay appears here, above input field with slide animation */}
            <Collapse in={!!parsedFormSpec && formExpanded} timeout={400} unmountOnExit>
              <Box data-component="form-container">
                <FormOverlay
                  formSpec={parsedFormSpec || { fields: [] }}
                  onSubmit={handleFormSubmit}
                  canAddNotes={canAddNotes}
                  existingNotes={formNotes}
                  onSaveNote={handleSaveFormNote}
                  messageIndex={activeFormMessageIndex}
                  onMinimize={() => setFormExpanded(false)}
                  disabled={immediateThinking || isProcessing}
                />
              </Box>
            </Collapse>

            {/* Form couldn't be parsed - show a visible fallback instead of silently dropping it */}
            {formParseError && !parsedFormSpec && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                This form couldn't be displayed due to a formatting issue. Please ask the assistant to resend it.
              </Alert>
            )}

            {/* Collapsed form bar - clickable to expand */}
            {parsedFormSpec && !formExpanded && (
              <Box
                onClick={() => setFormExpanded(true)}
                sx={{
                  mb: 1,
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Assignment sx={{ fontSize: 20 }} />
                  <Typography variant="body2" fontWeight="medium">
                    {parsedFormSpec.title || 'Fill out form'}
                  </Typography>
                </Box>
                <ExpandLess sx={{ fontSize: 20 }} />
              </Box>
            )}

            {/* Quick reply chips */}
            <Collapse in={!!activeQuickReplies && activeQuickReplies.length > 0} timeout={300} unmountOnExit>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                {activeQuickReplies?.map((text, idx) => (
                  <Chip
                    key={idx}
                    label={text}
                    variant="outlined"
                    clickable
                    disabled={immediateThinking || isProcessing}
                    onClick={() => handleQuickReplyClick(text)}
                    sx={{
                      borderRadius: '16px',
                      fontSize: '0.875rem',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  />
                ))}
              </Box>
            </Collapse>

            <Box
              data-component="text-input-wrapper"
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 3,
                bgcolor: (submitting || !canRespond() || immediateThinking || isProcessing)
                  ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)')
                  : (theme.palette.mode === 'dark' ? '#30353e' : '#fff'),
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 1px 4px rgba(0, 0, 0, 0.45)'
                  : '0 1px 4px rgba(0, 0, 0, 0.15)',
                '&:focus-within': {
                  borderColor: 'primary.main',
                },
              }}
            >
              <TextField
                fullWidth
                multiline
                minRows={1}
                maxRows={6}
                placeholder={canRespond() ? "Type your response here..." : getReplyDisabledMessage()}
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                onKeyDown={handleKeyPress}
                onFocus={() => { if (parsedFormSpec) setFormExpanded(true) }}
                disabled={submitting || !canRespond() || immediateThinking || isProcessing}
                variant="standard"
                sx={{
                  '& .MuiInput-root': {
                    px: 2,
                    pt: 2,
                    pb: 1,
                    fontSize: '0.95rem',
                    '&::before, &::after': {
                      display: 'none',
                    },
                  },
                  '& .MuiInput-input': {
                    py: 0,
                  },
                }}
              />

              {/* Toolbar row: action buttons */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 0.75 }}>
                {/* Left side: attachment + more options */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {agentAcceptsFiles && (
                    <IconButton
                      onClick={() => fileInputRef.current?.click()}
                      disabled={submitting || !canRespond() || immediateThinking || isProcessing}
                      size="small"
                      sx={{ color: 'text.secondary' }}
                    >
                      <AttachFile sx={{ fontSize: 20 }} />
                    </IconButton>
                  )}

                  <>
                    <IconButton
                      onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                      size="small"
                      sx={{ color: 'text.secondary' }}
                    >
                      <MoreVert sx={{ fontSize: 20 }} />
                    </IconButton>

                    <Menu
                      anchorEl={moreMenuAnchor}
                      open={Boolean(moreMenuAnchor)}
                      onClose={() => setMoreMenuAnchor(null)}
                      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    >
                      {agentAcceptsFiles && (
                        <MenuItem
                          onClick={() => {
                            setMoreMenuAnchor(null)
                            setAttachmentsModalOpen(true)
                          }}
                          disabled={!displayTicket?.data?.external_attachments?.length}
                        >
                          <ListItemIcon>
                            <CloudDownload fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Download Attachments</ListItemText>
                        </MenuItem>
                      )}
                      {isAdmin && (
                        <MenuItem
                          onClick={() => {
                            toggleSystemInfo()
                            setMoreMenuAnchor(null)
                          }}
                        >
                          <ListItemIcon>
                            <InfoOutlined fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>
                            {showSystemInfo ? 'Hide System Info' : 'Show System Info'}
                          </ListItemText>
                        </MenuItem>
                      )}
                      {isAdmin && (
                        <MenuItem onClick={handleViewRenderedPrompt}>
                          <ListItemIcon>
                            <DataObject fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>View Rendered Prompt</ListItemText>
                        </MenuItem>
                      )}
                      {isAdmin && (
                        <MenuItem onClick={() => { setMoreMenuAnchor(null); setReviewOpen(true) }}>
                          <ListItemIcon>
                            <FactCheck fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>AI Review</ListItemText>
                        </MenuItem>
                      )}
                      <MenuItem
                        onClick={() => {
                          setPendingLanguage(user?.preferred_language || 'en')
                          setLanguageDialogOpen(true)
                          setMoreMenuAnchor(null)
                        }}
                      >
                        <ListItemIcon>
                          <Language fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Change Language</ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                </Box>

                {/* Right side: send button */}
                <IconButton
                  color="primary"
                  onClick={handleSubmitResponse}
                  disabled={!response.trim() || submitting || !canRespond() || immediateThinking || isProcessing}
                  size="small"
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    p: 0.75,
                    '&:hover': { bgcolor: 'primary.dark' },
                    '&:disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
                  }}
                >
                  <ArrowUpward sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>

              {/* Hidden file input */}
              {agentAcceptsFiles && (
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  onChange={handleFileSelect}
                  accept="*/*"
                />
              )}
            </Box>
          </Box>
        </Box>}

      {/* Change Language Dialog */}
      <Dialog open={languageDialogOpen} onClose={() => setLanguageDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Language</DialogTitle>
        <DialogContent>
          <SearchableSelect
            label="Preferred Language"
            value={pendingLanguage}
            onChange={(e) => setPendingLanguage(e.target.value)}
            options={LANGUAGE_OPTIONS}
            formControlProps={{ sx: { mt: 1 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLanguageDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveLanguage} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={archiveDialogOpen}
        onClose={() => setArchiveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Archive Ticket</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to archive ticket #{ticket?.id}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleArchiveTicket}
            color="warning"
            variant="contained"
            disabled={archiving}
          >
            {archiving ? 'Archiving...' : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Permanently Delete Ticket</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this item? This is a permanent action and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteTicket}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drag and Drop Overlay - positioned at main container level */}
      {isDragging && agentAcceptsFiles && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(25, 118, 210, 0.1)',
            border: '3px dashed',
            borderColor: 'primary.main',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        >
          {/* White card background for better readability */}
          <Paper
            elevation={3}
            sx={{
              p: 4,
              bgcolor: 'background.paper',
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CloudUpload sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" color="primary">
              Drop file here to upload
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              The file will be sent to the agent for processing
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Attachments Modal */}
      <AttachmentsModal
        open={attachmentsModalOpen}
        onClose={() => setAttachmentsModalOpen(false)}
        ticket={displayTicket}
      />

      {/* Notes Modal */}
      <Dialog
        open={notesModalOpen}
        onClose={handleCloseNotesModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Message Notes
          {selectedMessageIndex !== null && (
            <Typography variant="caption" display="block" color="text.secondary">
              Message #{selectedMessageIndex + 1}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedMessageIndex !== null && (
            <>
              {/* Display existing notes for this message */}
              {getNotesForMessage(selectedMessageIndex).length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Existing Notes:
                  </Typography>
                  {getNotesForMessage(selectedMessageIndex).map((note, idx) => (
                    <Paper key={note.id || idx} sx={{ p: 2, mb: 1, bgcolor: 'grey.50' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {note.user_name || note.user_email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {note.created_at ? new Date(note.created_at).toLocaleString() : ''}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {note.note}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* Add new note */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Add New Note:
                </Typography>
                <TextField
                  autoFocus
                  fullWidth
                  multiline
                  rows={4}
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Enter your note here..."
                  variant="outlined"
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNotesModal}>
            Close
          </Button>
          <Button
            onClick={handleSaveNote}
            color="primary"
            variant="contained"
            disabled={savingNote || !noteText.trim()}
          >
            {savingNote ? 'Saving...' : 'Save Note'}
          </Button>
        </DialogActions>
      </Dialog>
    {isAdmin && (
      <TicketReviewPanel open={reviewOpen} onClose={() => setReviewOpen(false)} ticketId={ticket?.id} />
    )}
    <Dialog
      open={renderedPromptsOpen}
      onClose={() => setRenderedPromptsOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Rendered Prompts</DialogTitle>
      <DialogContent>
        {renderedPromptsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {renderedPromptsError && (
          <Alert severity="error">{renderedPromptsError}</Alert>
        )}
        {renderedPromptsData && renderedPromptsData.map((entry, i) => (
          <Accordion
            key={entry.agent_name}
            expanded={renderedPromptsExpanded === i}
            onChange={() => setRenderedPromptsExpanded(renderedPromptsExpanded === i ? -1 : i)}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {entry.agent_name}
                </Typography>
                {entry.is_orchestrator && (
                  <Chip label="Orchestrator" size="small" color="primary" variant="outlined" />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Box
                sx={{
                  overflow: 'auto',
                  fontSize: 13,
                  m: 0,
                  p: 2,
                  bgcolor: 'grey.50',
                  // Keep the markdown compact and readable; code fences (```form etc.)
                  // render as plain code blocks, not interactive widgets.
                  '& p': { my: 0.5 },
                  '& h1, & h2, & h3': { mt: 1.5, mb: 0.5 },
                  '& ul, & ol': { my: 0.5, pl: 2.5 },
                  '& pre': {
                    whiteSpace: 'pre-wrap',
                    bgcolor: 'grey.100',
                    p: 1,
                    borderRadius: 1,
                    overflow: 'auto',
                    fontSize: 11,
                  },
                  '& code': { fontFamily: 'monospace', fontSize: 11 },
                }}
              >
                {entry.prompt
                  ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{entry.prompt}</ReactMarkdown>
                  : '(empty)'}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
        {renderedPromptsData && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Rendered using the canvas version active when this conversation started. If that version was deleted, the current active version was used as a fallback.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setRenderedPromptsOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
    {!isMobile && (
      <Tooltip title={showInfoPanel ? 'Hide info panel' : 'Show info panel'} placement="left">
        <Box
          onClick={() => {
            const next = !showInfoPanel
            setShowInfoPanel(next)
            localStorage.setItem('agentflow-show-info-panel', JSON.stringify(next))
          }}
          sx={{
            position: 'absolute',
            right: showInfoPanel ? 340 : 0,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 80,
            bgcolor: showInfoPanel ? 'primary.main' : 'background.paper',
            borderRadius: '6px 0 0 6px',
            cursor: 'pointer',
            border: 1,
            borderRight: 0,
            borderColor: showInfoPanel ? 'primary.main' : 'divider',
            boxShadow: showInfoPanel ? 'none' : '-2px 0 6px rgba(0,0,0,0.12)',
            zIndex: 11,
            '&:hover': { bgcolor: showInfoPanel ? 'primary.dark' : 'grey.100' },
            transition: 'background-color 0.15s, right 0.2s',
          }}
        >
          <InfoOutlined sx={{ fontSize: 14, color: showInfoPanel ? 'white' : 'primary.main' }} />
        </Box>
      </Tooltip>
    )}
    {!isMobile && (
      <Box sx={{
        position: 'absolute', right: 0, top: 0, bottom: 0, width: 340, zIndex: 10,
        transform: showInfoPanel ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.2s ease',
      }}>
        <InfoSidePanel
          ticket={ticket}
          isProcessing={isProcessing}
          streamingLogs={streamingLogs}
          onClose={() => {
            setShowInfoPanel(false)
            localStorage.setItem('agentflow-show-info-panel', JSON.stringify(false))
          }}
        />
      </Box>
    )}
    </Box>
    </Box>
    </Box>
  )
}

export default ConversationView
